import { useEffect, useState, useCallback } from 'react';
import { MobileHeader, HeaderAction } from '../components/MobileHeader';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowLeft, Instagram, Phone, Mail, Edit2, Trash2, Plus, CheckCircle, CalendarDays, Archive,
  Wallet, TrendingDown, TrendingUp, ReceiptText
} from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../i18n';
import { api, clearApiCache } from '../api';
import { Avatar } from './Dashboard';
import StudentModal from '../components/StudentModal';
import PaymentModal from '../components/PaymentModal';
import ClassModal from '../components/ClassModal';
import { cn } from '../lib/utils';

// ── CompteTab — centre financier CHF ─────────────────────────────────────────
function CompteTab({ studentId, balanceData, balancePayments, attendancesDetail, onRefresh }) {
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount_chf: '', method: 'cash', notes: '' });
  const [saving, setSaving] = useState(false);
  const [overrideId, setOverrideId] = useState(null);
  const [overrideVal, setOverrideVal] = useState('');

  const bal = balanceData?.balance ?? 0;
  const balColor = bal >= 0 ? 'text-[#26A69A]' : 'text-red-500';

  const resetForm = () => setForm({ date: new Date().toISOString().slice(0, 10), amount_chf: '', method: 'cash', notes: '' });

  const handleSavePayment = async () => {
    if (!form.amount_chf || isNaN(parseFloat(form.amount_chf))) return;
    setSaving(true);
    try {
      if (editPayment) {
        await api.put(`/balance-payments/${editPayment.id}`, { ...form, amount_chf: parseFloat(form.amount_chf) });
        toast.success('Paiement modifié');
      } else {
        await api.post('/balance-payments', { student_id: studentId, ...form, amount_chf: parseFloat(form.amount_chf) });
        toast.success('Paiement enregistré');
      }
      setShowAddPayment(false); setEditPayment(null); resetForm();
      onRefresh();
    } finally { setSaving(false); }
  };

  const handleDeletePayment = async (pid) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    await api.delete(`/balance-payments/${pid}`);
    toast.success('Paiement supprimé'); onRefresh();
  };

  const handleOverrideSave = async (attId) => {
    const v = overrideVal === '' ? null : parseFloat(overrideVal);
    await api.patch(`/attendances/${attId}/override`, { amount_override: v });
    toast.success(v === null ? 'Override supprimé' : 'Tarif modifié');
    setOverrideId(null); setOverrideVal(''); onRefresh();
  };

  const METHODS = { cash: 'Espèces', twint: 'Twint', virement: 'Virement', autre: 'Autre' };

  return (
    <div className="space-y-4" data-testid="compte-tab">
      {/* Balance summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Solde départ', value: balanceData?.balance_start_chf ?? 0, icon: Wallet, color: 'text-slate-600' },
          { label: 'Total crédits', value: balanceData?.total_payments ?? 0, icon: TrendingUp, color: 'text-[#26A69A]' },
          { label: 'Total débits', value: -(balanceData?.total_debits ?? 0), icon: TrendingDown, color: 'text-red-500' },
          { label: 'Solde actuel', value: bal, icon: ReceiptText, color: balColor },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-3 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-1.5 mb-1">
              <Icon size={13} className={color} />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
            </div>
            <div className={cn('text-xl font-extrabold', color)}>{value >= 0 ? '+' : ''}{value.toFixed(2)}</div>
            <div className="text-[10px] text-slate-400">CHF</div>
          </div>
        ))}
      </div>

      {/* Payments section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-50">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Paiements</span>
          <button onClick={() => { resetForm(); setEditPayment(null); setShowAddPayment(v => !v); }}
            className="flex items-center gap-1 text-xs text-[#0288D1] font-semibold hover:bg-[#0288D1]/5 px-2 py-1 rounded-lg transition-colors"
            data-testid="add-balance-payment-btn">
            <Plus size={13} /> Ajouter
          </button>
        </div>

        {(showAddPayment || editPayment) && (
          <div className="px-4 py-3 bg-[#0288D1]/5 border-b border-[#0288D1]/10 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-slate-500 mb-1 block">Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}
                  className="w-full h-9 px-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-[#0288D1]" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 mb-1 block">Montant CHF</label>
                <input type="number" step="0.5" min="0" placeholder="0.00" value={form.amount_chf}
                  onChange={e => setForm(f => ({...f, amount_chf: e.target.value}))}
                  className="w-full h-9 px-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-[#0288D1]"
                  data-testid="payment-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-slate-500 mb-1 block">Moyen</label>
                <select value={form.method} onChange={e => setForm(f => ({...f, method: e.target.value}))}
                  className="w-full h-9 px-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-[#0288D1]">
                  {Object.entries(METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 mb-1 block">Notes</label>
                <input placeholder="Optionnel" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                  className="w-full h-9 px-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-[#0288D1]" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSavePayment} disabled={saving}
                className="flex-1 h-9 bg-[#0288D1] text-white text-sm font-bold rounded-xl disabled:opacity-50"
                data-testid="save-balance-payment-btn">
                {saving ? '…' : (editPayment ? 'Modifier' : 'Enregistrer')}
              </button>
              <button onClick={() => { setShowAddPayment(false); setEditPayment(null); resetForm(); }}
                className="h-9 px-4 bg-slate-100 text-slate-600 text-sm rounded-xl">Annuler</button>
            </div>
          </div>
        )}

        <div className="divide-y divide-slate-50">
          {balancePayments.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm">Aucun paiement enregistré</div>
          )}
          {balancePayments.map(p => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 group" data-testid="balance-payment-row">
              <div className="w-2 h-2 rounded-full bg-[#26A69A] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-[#26A69A]">+{parseFloat(p.amount_chf).toFixed(2)} CHF</span>
                  <span className="text-xs text-slate-400">{p.date}</span>
                  <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">{METHODS[p.method] || p.method}</span>
                </div>
                {p.notes && <div className="text-xs text-slate-400 truncate">{p.notes}</div>}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setEditPayment(p); setForm({ date: p.date, amount_chf: p.amount_chf, method: p.method || 'cash', notes: p.notes || '' }); setShowAddPayment(false); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#0288D1] hover:bg-[#0288D1]/5 transition-colors">
                  <Edit2 size={12} />
                </button>
                <button onClick={() => handleDeletePayment(p.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  data-testid={`delete-payment-${p.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Attendances detail */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-50">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Historique des présences</span>
        </div>
        <div className="divide-y divide-slate-50">
          {attendancesDetail.filter(a => a.present).length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm">Aucune présence enregistrée</div>
          )}
          {attendancesDetail.filter(a => a.present).map(att => (
            <div key={att.id || att.class_slot_id} className="flex items-center gap-3 px-4 py-2.5 group" data-testid="att-detail-row">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800 truncate">{att.class_title}</span>
                  <span className="text-xs text-slate-400">{att.class_date}</span>
                  {att.is_trial && <span className="text-[10px] font-bold text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded-full">Essai</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {overrideId === (att.id || att.class_slot_id) ? (
                  <div className="flex items-center gap-1">
                    <input type="number" step="0.5" min="0" value={overrideVal} onChange={e => setOverrideVal(e.target.value)}
                      className="w-16 h-7 text-center text-xs border border-[#0288D1] rounded-lg outline-none"
                      autoFocus onKeyDown={e => e.key === 'Enter' && handleOverrideSave(att.id)} />
                    <button onClick={() => handleOverrideSave(att.id)} className="text-xs font-bold text-[#26A69A]">✓</button>
                    <button onClick={() => { setOverrideId(null); setOverrideVal(''); }} className="text-xs text-slate-400">✕</button>
                  </div>
                ) : (
                  <>
                    <span className={cn('text-sm font-bold', att.effective_amount === 0 ? 'text-slate-400' : 'text-red-500')}>
                      {att.effective_amount > 0 ? `-${att.effective_amount.toFixed(2)}` : '0.00'} CHF
                    </span>
                    {att.amount_override !== null && att.amount_override !== undefined && (
                      <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded">Override</span>
                    )}
                    {att.id && (
                      <button onClick={() => { setOverrideId(att.id); setOverrideVal(att.amount_override ?? att.amount_frozen ?? ''); }}
                        className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded text-slate-300 hover:text-amber-500 transition-all"
                        title="Modifier le tarif pour cette présence" data-testid={`override-att-${att.id}`}>
                        <Edit2 size={11} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const locale = lang === 'fr' ? fr : undefined;

  const [student, setStudent] = useState(null);
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [financeData, setFinanceData] = useState(null);
  const [groupBalance, setGroupBalance] = useState(null);
  const [tab, setTab] = useState('compte');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showPkgForm, setShowPkgForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [groupCourses, setGroupCourses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [editingClass, setEditingClass] = useState(null);
  // Balance (new system)
  const [balanceData, setBalanceData] = useState(null);
  const [balancePayments, setBalancePayments] = useState([]);
  const [attendancesDetail, setAttendancesDetail] = useState([]);
  const [pricingMatrix, setPricingMatrix] = useState([]);
  const [billingLogs, setBillingLogs] = useState([]);
  // Inline editing for dance profile fields
  const [inlineField, setInlineField] = useState(null); // 'dance_background' | 'experience_years' | 'notes'
  const [inlineVal, setInlineVal] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      // v2.13 : tout ce qui concerne l'élève en UNE demande ; les grandes listes viennent de la mémoire (préchargées).
      const [f, fin, gc, locs, sts, matrix] = await Promise.all([
        api.get(`/students/${id}/fiche`),
        api.get('/finance'),
        api.get('/group-courses'),
        api.get('/locations'),
        api.get('/students'),
        api.get('/pricing-matrix').catch(() => []),
      ]);
      if (f.assigned) clearApiCache();  // des séances ont été rangées : les soldes ont pu changer
      setStudent(f.student);
      setPackages(f.packages);
      setPayments(f.payments);
      setFinanceData(fin.find(x => x.student?.id === id));
      setAttendances(f.classes);
      setGroupBalance(f.group_balance);
      setGroupCourses(gc);
      setLocations(locs);
      setAllStudents(sts);
      setBalanceData(f.balance);
      setBalancePayments(f.balance_payments || []);
      setAttendancesDetail(f.attendances_detail || []);
      setPricingMatrix(matrix || []);
      setBillingLogs(f.billing_logs || []);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveInlineField = async (field, val) => {
    const payload = { [field]: field === 'experience_years' ? (parseInt(val, 10) || 0) : val };
    try {
      await api.put(`/students/${id}`, payload);
      setStudent(s => ({ ...s, ...payload }));
      toast.success('Sauvegardé');
    } catch { toast.error('Erreur'); }
    setInlineField(null);
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cet élève ?')) return;
    await api.delete(`/students/${id}`);
    toast.success('Élève supprimé');
    navigate('/students');
  };

  const handleDeletePayment = async (pid) => {
    await api.delete(`/payments/${pid}`);
    toast.success('Paiement supprimé');
    fetchAll();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">{t('common.loading')}</div>;
  if (!student) return <div className="text-center py-20 text-slate-400">Élève introuvable</div>;

  const fin = financeData;

  return (
    <div className="max-w-2xl mx-auto" data-testid="student-profile">
      <MobileHeader title={`${student.first_name} ${student.last_name}`} back actions={<>
        <HeaderAction icon={Edit2} label="Modifier" onClick={() => setShowEditModal(true)} testId="edit-student-btn-mobile" />
        <HeaderAction icon={Trash2} label="Supprimer" onClick={handleDelete} testId="delete-student-btn-mobile" />
      </>} />
      {/* Header */}
      <div className="mobile-hidden flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} className="text-slate-600" />
        </button>
        <h1 className="text-2xl font-extrabold text-slate-900 font-manrope flex-1">
          {student.first_name} {student.last_name}
        </h1>
        <button
          onClick={() => setShowEditModal(true)}
          className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
          data-testid="edit-student-btn"
        >
          <Edit2 size={16} className="text-slate-600" />
        </button>
        <button
          onClick={handleDelete}
          className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl bg-white border border-red-100 hover:bg-red-50 transition-colors"
          data-testid="delete-student-btn"
        >
          <Trash2 size={16} className="text-red-500" />
        </button>
      </div>

      {/* Identity card */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm mb-4">
        <div className="flex items-center gap-4">
          <Avatar name={`${student.first_name} ${student.last_name}`} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg text-slate-900 font-manrope">
              {student.first_name} {student.last_name}
            </div>
            {student.role && (
              <span className={cn(
                'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold mt-1',
                student.role === 'leader' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'
              )}>
                {student.role === 'leader' ? 'Leader' : 'Follower'}
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {student.email && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail size={14} className="text-slate-400" />
              <a href={`mailto:${student.email}`} className="hover:text-[#0288D1]">{student.email}</a>
            </div>
          )}
          {student.phone && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Phone size={14} className="text-slate-400" />
              <a href={`tel:${student.phone}`} className="hover:text-[#0288D1]">{student.phone}</a>
            </div>
          )}
          {student.instagram && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Instagram size={14} className="text-slate-400" />
              <span>{student.instagram}</span>
            </div>
          )}
          {student.enrolled_at && (
            <div className="text-xs text-slate-400 mt-2">
              {t('students.enrolled')} {format(parseISO(student.enrolled_at), 'd MMM yyyy', { locale })}
            </div>
          )}
        </div>

        {/* ── Section Profil danse (toujours visible, édition inline) ── */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Profil danse</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Style de danse */}
            <div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('students.background')}</div>
              {inlineField === 'dance_background' ? (
                <div className="flex gap-1">
                  <input autoFocus value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveInlineField('dance_background', inlineVal); if (e.key === 'Escape') setInlineField(null); }}
                    className="flex-1 text-sm border border-[#0288D1]/40 rounded-lg px-2 py-1 focus:outline-none focus:border-[#0288D1]"
                    placeholder="Bachata, Zouk..."
                    data-testid="inline-background-input"
                  />
                  <button onClick={() => saveInlineField('dance_background', inlineVal)} className="text-[#0288D1] hover:text-[#0277BE] px-1">✓</button>
                  <button onClick={() => setInlineField(null)} className="text-slate-400 hover:text-slate-600 px-1">✕</button>
                </div>
              ) : (
                <button onClick={() => { setInlineVal(student.dance_background || ''); setInlineField('dance_background'); }}
                  className="w-full text-left text-sm rounded-lg px-2 py-1 hover:bg-slate-50 transition-colors group"
                  data-testid="inline-background-btn">
                  {student.dance_background
                    ? <span className="text-slate-700">{student.dance_background}</span>
                    : <span className="text-slate-300 italic">— à renseigner</span>}
                </button>
              )}
            </div>
            {/* Expérience */}
            <div>
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('students.exp')}</div>
              {inlineField === 'experience_years' ? (
                <div className="flex gap-1">
                  <input autoFocus type="number" min="0" max="50" value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveInlineField('experience_years', inlineVal); if (e.key === 'Escape') setInlineField(null); }}
                    className="flex-1 text-sm border border-[#0288D1]/40 rounded-lg px-2 py-1 focus:outline-none focus:border-[#0288D1]"
                    data-testid="inline-exp-input"
                  />
                  <button onClick={() => saveInlineField('experience_years', inlineVal)} className="text-[#0288D1] hover:text-[#0277BE] px-1">✓</button>
                  <button onClick={() => setInlineField(null)} className="text-slate-400 hover:text-slate-600 px-1">✕</button>
                </div>
              ) : (
                <button onClick={() => { setInlineVal(String(student.experience_years || 0)); setInlineField('experience_years'); }}
                  className="w-full text-left text-sm rounded-lg px-2 py-1 hover:bg-slate-50 transition-colors"
                  data-testid="inline-exp-btn">
                  {student.experience_years > 0
                    ? <span className="text-slate-700">{student.experience_years} {t('students.exp')}</span>
                    : <span className="text-slate-300 italic">— à renseigner</span>}
                </button>
              )}
            </div>
          </div>
          {/* Notes / Observations */}
          <div className="mb-3">
            {/* Statut tarifaire (cours privés) */}
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Statut tarifaire privé</div>
            <select
              value={student.billing_status || 'Standard'}
              onChange={async e => {
                const val = e.target.value;
                await api.put(`/students/${id}`, { billing_status: val });
                setStudent(s => ({ ...s, billing_status: val }));
                setBillingLogs(prev => [{
                  id: Date.now().toString(),
                  changed_at: new Date().toISOString(),
                  old_value: student.billing_status || 'Standard',
                  new_value: val,
                  context: 'Profil étudiant (cours privés)',
                }, ...prev]);
                toast.success('Statut tarifaire mis à jour');
              }}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#0288D1] bg-white"
              data-testid="student-billing-status-select"
            >
              {(pricingMatrix.length > 0 ? pricingMatrix : [{ status_name: 'Standard' }]).map(r => (
                <option key={r.status_name} value={r.status_name}>{r.status_name}</option>
              ))}
            </select>
            {/* Log des changements de profil financier */}
            {billingLogs.length > 0 && (
              <div className="mt-2 space-y-0.5" data-testid="billing-status-log">
                <div className="text-[9px] font-bold text-slate-300 uppercase tracking-wider mb-1">Historique</div>
                {billingLogs.slice(0, 5).map(log => (
                  <div key={log.id} className="flex items-center gap-1.5 text-[9px] text-slate-400 py-0.5">
                    <span className="text-slate-300 font-mono">
                      {log.changed_at ? log.changed_at.slice(0, 10) : ''}
                    </span>
                    <span className="text-slate-300">·</span>
                    <span className="line-through text-slate-300">{log.old_value}</span>
                    <span className="text-slate-300">→</span>
                    <span className="font-semibold text-slate-500">{log.new_value}</span>
                    {log.context && log.context !== 'Profil étudiant (cours privés)' && (
                      <span className="text-[8px] text-slate-300 truncate max-w-[80px]" title={log.context}>
                        {log.context}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Notes / Observations */}
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t('students.notes')}</div>
            {inlineField === 'notes' ? (
              <div>
                <textarea autoFocus rows={3} value={inlineVal} onChange={e => setInlineVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setInlineField(null); }}
                  className="w-full text-sm border border-[#0288D1]/40 rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#0288D1] resize-none"
                  placeholder="Observations, message du portail d'inscription…"
                  data-testid="inline-notes-input"
                />
                <div className="flex gap-2 mt-1 justify-end">
                  <button onClick={() => setInlineField(null)} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1">Annuler</button>
                  <button onClick={() => saveInlineField('notes', inlineVal)} className="text-xs font-semibold text-[#0288D1] hover:text-[#0277BE] px-2 py-1">Enregistrer</button>
                </div>
              </div>
            ) : (
              <button onClick={() => { setInlineVal(student.notes || ''); setInlineField('notes'); }}
                className="w-full text-left text-sm rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors min-h-[2rem]"
                data-testid="inline-notes-btn">
                {student.notes
                  ? <span className="text-slate-600 whitespace-pre-wrap">{student.notes}</span>
                  : <span className="text-slate-300 italic">— aucune observation</span>}
              </button>
            )}
          </div>
        </div>
      </div>


      {/* ── Indicateurs clés ── */}
      {(() => {
        const totalDue = fin?.total_remaining ?? 0;
        const classesLeft = fin?.classes_remaining ?? 0;
        const hoursLeft = fin?.hours_remaining ?? 0;
        const grpDone = fin?.group_hours_done ?? 0;
        const privDone = fin?.private_hours_done ?? 0;
        const hasPackage = packages.filter(p => p.is_active).length > 0;

        const bal = balanceData?.balance ?? 0;
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            {/* 1 — Solde actuel (balance temps réel) */}
            <div className={cn(
              'rounded-2xl p-3 border text-center',
              bal < 0 ? 'bg-red-50 border-red-100' : 'bg-[#26A69A]/5 border-[#26A69A]/20'
            )} data-testid="due-indicator">
              <div className={cn('text-xl font-extrabold font-manrope leading-none mb-0.5 tabular-nums',
                bal < 0 ? 'text-red-500' : 'text-[#26A69A]')}>
                {bal > 0 ? '+' : ''}{bal.toFixed(0)}
              </div>
              <div className={cn('text-[10px] font-bold',
                bal < 0 ? 'text-red-400' : 'text-[#26A69A]')}>
                {bal < 0 ? 'CHF dûs' : 'CHF solde'}
              </div>
            </div>

            {/* 2 — Séances restantes dans le forfait */}
            <div className={cn(
              'rounded-2xl p-3 border text-center',
              hasPackage ? 'bg-[#0288D1]/5 border-[#0288D1]/20' : 'bg-slate-50 border-slate-100'
            )} data-testid="sessions-remaining-indicator">
              {hasPackage ? (
                <>
                  <div className="text-xl font-extrabold font-manrope leading-none mb-0.5 text-[#0288D1]">
                    {classesLeft > 0 ? classesLeft : hoursLeft > 0 ? `${hoursLeft}h` : '—'}
                  </div>
                  <div className="text-[10px] font-bold text-[#0288D1]">
                    {classesLeft > 0 ? 'cours restants' : hoursLeft > 0 ? 'restantes' : 'épuisé'}
                  </div>
                  {classesLeft > 0 && hoursLeft > 0 && (
                    <div className="text-[9px] text-slate-400 mt-0.5">{hoursLeft}h privées aussi</div>
                  )}
                  {hoursLeft > 0 && (
                    <div className="text-[9px] text-amber-500 font-semibold mt-0.5">à planifier</div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-xl font-extrabold font-manrope text-slate-300">—</div>
                  <div className="text-[10px] text-slate-400">pas de forfait</div>
                </>
              )}
            </div>

            {/* 3 — Total effectué */}
            <div className="rounded-2xl p-3 border bg-white border-slate-100 text-center" data-testid="sessions-done-indicator">
              <div className="text-xl font-extrabold font-manrope leading-none mb-0.5 text-slate-700">
                {grpDone > 0 || privDone > 0 ? (grpDone + privDone).toFixed(1) : '0'}
              </div>
              <div className="text-[10px] font-bold text-slate-500">h effectuées</div>
              <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">
                {grpDone > 0 && <div>{grpDone}h collectif</div>}
                {privDone > 0 && <div>{privDone}h privé</div>}
                {grpDone === 0 && privDone === 0 && <div>aucune séance</div>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment CTA */}
      <button
        onClick={() => setShowPayModal(true)}
        className="w-full mb-4 bg-[#26A69A] text-white h-12 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#219e92] active:scale-[0.98] transition-all"
        data-testid="add-payment-btn"
      >
        <Plus size={18} />
        {t('finance.addPayment')}
      </button>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-100 overflow-x-auto">
        {[
          ['compte', lang === 'fr' ? 'Compte CHF' : 'CHF Account'],
          ['courses', lang === 'fr' ? 'Cours suivis' : 'Classes'],
          ['packages', lang === 'fr' ? 'Forfaits & Paiements' : 'Packages & Payments'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'pb-2 px-1 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap',
              tab === key ? 'border-[#0288D1] text-[#0288D1]' : 'border-transparent text-slate-500 hover:text-slate-800'
            )}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'compte' && (
        <CompteTab
          studentId={id}
          balanceData={balanceData}
          balancePayments={balancePayments}
          attendancesDetail={attendancesDetail}
          onRefresh={fetchAll}
        />
      )}

      {tab === 'courses' && (
        <CoursesTab
          studentId={id}
          studentName={`${student.first_name} ${student.last_name}`}
          attendances={attendances}
          packages={packages}
          payments={payments}
          groupCourses={groupCourses}
          pricingMatrix={pricingMatrix}
          locale={locale}
          onRefresh={fetchAll}
          onEditClass={cls => setEditingClass(cls)}
        />
      )}

      {tab === 'packages' && (
        <div className="space-y-4">
          {/* Header with add button */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              {packages.filter(p => p.is_active && !p.archived_at).length} forfait{packages.filter(p => p.is_active && !p.archived_at).length !== 1 ? 's' : ''} actif{packages.filter(p => p.is_active && !p.archived_at).length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => setShowPkgForm(true)}
              className="flex items-center gap-1 text-xs text-[#0288D1] font-semibold hover:bg-[#0288D1]/5 px-2 py-1 rounded-lg transition-colors"
              data-testid="add-package-btn"
            >
              <Plus size={14} /> Nouveau forfait
            </button>
          </div>
          {showPkgForm && (
            <PackageForm studentId={id} onSaved={() => { setShowPkgForm(false); fetchAll(); }} onCancel={() => setShowPkgForm(false)} />
          )}

          {/* Active packages */}
          {packages.filter(p => p.is_active && !p.archived_at).length === 0 && !showPkgForm ? (
            <div className="text-sm text-slate-400 bg-white rounded-xl border border-slate-100 p-8 text-center">Aucun forfait actif</div>
          ) : (() => {
            const activePkgs = packages.filter(p => p.is_active && !p.archived_at)
              .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            const activeGroupPkgs = [...activePkgs]
              .filter(p => p.type === 'group' || p.type === 'mixed')
              .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
            return activePkgs.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                pkgPayments={payments.filter(p => p.package_id === pkg.id)}
                financeData={fin}
                siblingGroupPkgs={activeGroupPkgs}
                studentId={id}
                allAttendances={attendances}
                onRefresh={fetchAll}
                locale={locale}
              />
            ));
          })()}

          {/* Inactive (not yet started) packages */}
          {packages.filter(p => !p.is_active && !p.archived_at).map(pkg => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              pkgPayments={payments.filter(p => p.package_id === pkg.id)}
              financeData={fin}
              studentId={id}
              allAttendances={attendances}
              onRefresh={fetchAll}
              locale={locale}
            />
          ))}

          {/* Archived packages (passés) */}
          {packages.filter(p => p.archived_at).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Archive size={12} /> Forfaits passés ({packages.filter(p => p.archived_at).length})
              </div>
              {packages
                .filter(p => p.archived_at)
                .sort((a, b) => (b.archived_at || '').localeCompare(a.archived_at || ''))
                .map(pkg => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    pkgPayments={payments.filter(p => p.package_id === pkg.id)}
                    financeData={null}
                    studentId={id}
                    allAttendances={attendances}
                    onRefresh={fetchAll}
                    locale={locale}
                  />
                ))}
            </div>
          )}

          {/* Standalone payments (group courses + libre) */}
          {(() => {
            const standalone = payments.filter(p => !p.package_id)
              .sort((a, b) => b.date.localeCompare(a.date));
            if (standalone.length === 0) return null;
            return (
              <div className="mt-2">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Paiements hors forfait</div>
                <div className="space-y-2">
                  {standalone.map(p => {
                    const courseName = p.group_course_id
                      ? (groupBalance?.courses?.find(c => c.course_id === p.group_course_id)?.course_name || 'Cours collectif')
                      : null;
                    return (
                      <div key={p.id} className="bg-white rounded-xl px-4 py-3 border border-slate-100 flex items-center justify-between" data-testid="payment-row">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[#26A69A]">{p.amount} CHF</span>
                            {p.group_course_id
                              ? <span className="text-[10px] font-bold bg-[#0288D1]/10 text-[#0288D1] px-1.5 py-0.5 rounded-full">Collectif</span>
                              : <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Libre</span>
                            }
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {(() => { try { return format(parseISO(p.date), 'd MMM yyyy', { locale }); } catch { return p.date; } })()}
                            {' · '}{p.method}{courseName && ` · ${courseName}`}
                          </div>
                        </div>
                        <button onClick={() => handleDeletePayment(p.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors"
                          data-testid="delete-payment-btn">
                          <Trash2 size={13} className="text-red-400" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {showEditModal && (
        <StudentModal
          student={student}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); fetchAll(); }}
        />
      )}
      {showPayModal && (
        <PaymentModal
          studentId={id}
          studentName={`${student.first_name} ${student.last_name}`}
          packages={packages.filter(p => p.is_active)}
          groupCourses={groupBalance?.courses || []}
          onClose={() => setShowPayModal(false)}
          onSaved={() => { setShowPayModal(false); fetchAll(); }}
        />
      )}
      {editingClass && (
        <ClassModal
          classData={editingClass}
          locations={locations}
          allStudents={allStudents}
          groupCourses={groupCourses}
          onClose={() => setEditingClass(null)}
          onSaved={() => { setEditingClass(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────── SessionPayBtn ─────────────────────── */
function SessionPayBtn({ cls, studentId, existingPayments, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState('');
  const [saving, setSaving] = useState(false);
  const existing = existingPayments[0] ?? null;

  const handleOpen = () => {
    setAmt(existing ? String(existing.amount) : '');
    setOpen(true);
  };

  const handleSave = async () => {
    if (!amt || parseFloat(amt) <= 0) return;
    setSaving(true);
    try {
      if (existing) {
        await api.put(`/payments/${existing.id}`, { amount: parseFloat(amt) });
      } else {
        await api.post('/payments', {
          student_id: studentId,
          group_course_id: cls.group_course_id,
          amount: parseFloat(amt),
          method: 'cash',
          date: cls.datetime_start,
          notes: '',
        });
      }
      setOpen(false);
      onRefresh();
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!existing) return;
    setSaving(true);
    try {
      await api.delete(`/payments/${existing.id}`);
      setOpen(false);
      onRefresh();
    } finally { setSaving(false); }
  };

  if (open) {
    return (
      <div className="flex items-center gap-1" onKeyDown={e => e.key === 'Escape' && setOpen(false)}>
        <input autoFocus type="number" value={amt} onChange={e => setAmt(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          className="w-14 h-6 px-1.5 text-xs border border-[#26A69A]/40 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#26A69A]/40"
          placeholder="CHF" />
        <button onClick={handleSave} disabled={saving}
          className="w-6 h-6 flex items-center justify-center bg-[#26A69A] text-white rounded text-[10px] font-bold hover:bg-[#219e92]">
          {saving ? '…' : '✓'}
        </button>
        {existing && (
          <button onClick={handleDelete} disabled={saving}
            className="w-6 h-6 flex items-center justify-center bg-red-50 text-red-400 rounded text-[10px] hover:bg-red-100">
            ✗
          </button>
        )}
        <button onClick={() => setOpen(false)}
          className="w-6 h-6 flex items-center justify-center bg-slate-100 text-slate-400 rounded text-[10px]">
          ×
        </button>
      </div>
    );
  }

  if (existing) {
    return (
      <button onClick={handleOpen} data-testid="session-pay-amount"
        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#26A69A]/10 text-[#26A69A] hover:opacity-80 transition-opacity cursor-pointer">
        {existing.amount}.-
      </button>
    );
  }

  return (
    <button onClick={handleOpen} data-testid="session-pay-btn"
      className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-slate-300 hover:text-[#26A69A] hover:bg-[#26A69A]/5 transition-colors">
      CHF
    </button>
  );
}

/* ─────────────────────── CoursesTab ─────────────────────── */
function CoursesTab({ studentId, studentName, attendances, packages, payments, groupCourses, pricingMatrix, locale, onRefresh, onEditClass }) {
  const navigate = useNavigate();
  const [showInactiveEnrollments, setShowInactiveEnrollments] = useState(() => {
    try { return JSON.parse(localStorage.getItem('courses_tab_show_inactive')) || false; }
    catch { return false; }
  });
  const hasGroupPkg = packages.some(p => p.is_active && (p.type === 'group' || p.type === 'mixed'));
  const hasPrivPkg  = packages.some(p => p.is_active && (p.type === 'private' || p.type === 'mixed'));
  const now = new Date();

  const handleEnrollmentUpdate = async (courseId, updates) => {
    try {
      await api.put(`/group-courses/${courseId}/students/${studentId}`, updates);
      onRefresh();
      toast.success('Inscription mise à jour');
    } catch { toast.error('Erreur de mise à jour'); }
  };

  const goToPlanning = (cls) => {
    navigate('/planning', {
      state: { focusDate: cls.datetime_start, fromStudent: { id: studentId, name: studentName } }
    });
  };

  const getSessionPayments = (cls) => payments.filter(p =>
    p.group_course_id === cls.group_course_id &&
    p.date?.slice(0, 10) === cls.datetime_start?.slice(0, 10)
  );

  const fmtDate = (iso) => {
    try { return format(parseISO(iso), 'dd/MM/yy'); }
    catch { return iso?.slice(0, 10) || ''; }
  };

  // Group session row: date + pills on same line
  const SessionRow = ({ cls }) => {
    const att = cls.attendances?.find(a => a.student_id === studentId);
    const isCancelled = cls.status === 'cancelled';
    const isPast = new Date(cls.datetime_start) <= now;
    const sessionPays = getSessionPayments(cls);
    return (
      <div className="flex items-center gap-1.5 py-1.5 px-2.5 rounded-xl bg-white border border-slate-100 group" data-testid="attendance-row">
        <span className="text-xs font-semibold text-slate-800 whitespace-nowrap flex-1">{fmtDate(cls.datetime_start)}</span>
        {isCancelled
          ? <span className="text-[10px] font-bold text-red-400 bg-red-50 px-1.5 py-0.5 rounded-full">Annulé</span>
          : <>
              {isPast && !hasGroupPkg && (
                <SessionPayBtn cls={cls} studentId={studentId} existingPayments={sessionPays} onRefresh={onRefresh} />
              )}
              {isPast && hasGroupPkg && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#0288D1]/10 text-[#0288D1]">Forfait</span>
              )}
              <AttendancePairProfile classId={cls.id} studentId={studentId} att={att} onChanged={onRefresh} />
            </>
        }
        <button
          onClick={() => onEditClass(cls)}
          title="Modifier l'évènement"
          className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-[#0288D1] hover:bg-[#0288D1]/10 shrink-0"
          data-testid="open-editor-btn"
        >
          <CalendarDays size={11} />
        </button>
      </div>
    );
  };

  // Private session row: date + time + duration, pills on same line
  const PrivRow = ({ cls }) => {
    const att = cls.attendances?.find(a => a.student_id === studentId);
    const isCancelled = cls.status === 'cancelled';
    const timeStr = (() => { try { return format(parseISO(cls.datetime_start), 'HH:mm'); } catch { return ''; } })();
    const durMin = cls.duration_minutes;
    const dur = durMin ? `${Math.floor(durMin / 60)}h${durMin % 60 || ''}` : '';
    return (
      <div className="py-1.5 px-2.5 rounded-xl bg-white border border-slate-100 group" data-testid="attendance-row-private">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-800 whitespace-nowrap flex-1">{fmtDate(cls.datetime_start)}</span>
          {isCancelled
            ? <span className="text-[10px] font-bold text-red-400 bg-red-50 px-1.5 py-0.5 rounded-full">Annulé</span>
            : <>
                {hasPrivPkg
                  ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#26A69A]/10 text-[#26A69A]">Forfait</span>
                  : cls.session_price
                    ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600">{cls.session_price} CHF</span>
                    : null
                }
                <AttendancePairProfile classId={cls.id} studentId={studentId} att={att} onChanged={onRefresh} />
              </>
          }
          <button
            onClick={() => onEditClass(cls)}
            title="Modifier l'évènement"
            className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-[#26A69A] hover:bg-[#26A69A]/10 shrink-0"
            data-testid="open-editor-btn"
          >
            <CalendarDays size={11} />
          </button>
        </div>
        {(timeStr || dur) && (
          <div className="flex items-center gap-1.5 mt-0.5 pl-0.5">
            {timeStr && <span className="text-[10px] text-slate-400 font-medium">{timeStr}</span>}
            {timeStr && dur && <span className="text-[10px] text-slate-300">·</span>}
            {dur && <span className="text-[10px] text-slate-400 font-medium">{dur}</span>}
          </div>
        )}
      </div>
    );
  };

  const groupAll = attendances.filter(c => c.type === 'group');
  const privAll  = attendances.filter(c => c.type === 'private');

  // Cours depuis les inscriptions ET depuis l'historique de présences
  const enrolledFromCourses = groupCourses
    .filter(gc => gc.students?.some(s => s.student_id === studentId))
    .map(gc => gc.id);
  const enrolledFromAttendance = [...new Set(groupAll.map(c => c.group_course_id).filter(Boolean))];
  const allGroupCourseIds = [...new Set([...enrolledFromCourses, ...enrolledFromAttendance])];

  const hasPrivate = privAll.length > 0;

  // Build unified column data
  const allCols = [
    ...allGroupCourseIds.map(gcId => {
      const gc = groupCourses.find(c => c.id === gcId);
      const enrollment = gc?.students?.find(s => s.student_id === studentId);
      const isEnrollActive = enrollment ? enrollment.status !== 'inactive' : true;
      const color = gc?.color || '#0288D1';
      const name = gc?.name || 'Cours collectif';
      const sessions = groupAll.filter(c => c.group_course_id === gcId);
      const sample = sessions[0];
      const venue = gc?.location?.venue_name || gc?.location?.city || sample?.location?.venue_name || sample?.location?.city || '';
      const durMin = gc?.duration_minutes || sample?.duration_minutes;
      const durStr = durMin ? `${Math.floor(durMin / 60)}h${durMin % 60 || ''}` : '';
      const past = sessions.filter(c => new Date(c.datetime_start) <= now).sort((a, b) => b.datetime_start.localeCompare(a.datetime_start));
      const future = sessions.filter(c => new Date(c.datetime_start) > now).sort((a, b) => a.datetime_start.localeCompare(b.datetime_start));
      return { type: 'group', id: gcId, color, name, venue, durStr, sessions, past, future, enrollment, isEnrollActive, gc };
    }),
    ...(hasPrivate ? [{
      type: 'private', id: 'private', color: '#26A69A', name: 'Privés',
      venue: '', durStr: '', sessions: privAll,
      past: privAll.filter(c => new Date(c.datetime_start) <= now).sort((a, b) => b.datetime_start.localeCompare(a.datetime_start)),
      future: privAll.filter(c => new Date(c.datetime_start) > now).sort((a, b) => a.datetime_start.localeCompare(b.datetime_start)),
      totalPastMinutes: privAll.filter(c => new Date(c.datetime_start) <= now).reduce((acc, c) => acc + (c.duration_minutes || 0), 0),
      isEnrollActive: true,
    }] : []),
  ];

  const activeCols = allCols.filter(col => col.type === 'private' || col.isEnrollActive);
  const inactiveCols = allCols.filter(col => col.type === 'group' && !col.isEnrollActive);
  const displayCols = showInactiveEnrollments ? allCols : activeCols;

  if (displayCols.length === 0 && inactiveCols.length === 0) {
    return <div className="text-xs text-slate-400 text-center py-8">Aucun cours</div>;
  }

  const colWidths = Array(displayCols.length).fill('minmax(0, 1fr)').join(' ');

  // Mobile: each course stacked as a card
  const MobileView = () => (
    <div className="space-y-6">
      {displayCols.map(col => (
        <div key={col.id} className={cn('bg-white rounded-2xl border shadow-sm overflow-hidden',
          col.isEnrollActive === false ? 'border-slate-200 opacity-75' : 'border-slate-100')}>
          {/* Header */}
          <div className="px-3 pt-3 pb-2 border-b-2" style={{ borderBottomColor: col.color }}>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
              <span className="text-xs font-bold" style={{ color: col.color }}>{col.name}</span>
              {col.isEnrollActive === false && <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 rounded-full ml-1">inactif</span>}
              <span className="text-[10px] text-slate-400 ml-auto">{col.sessions.length}</span>
            </div>
            {col.type === 'group' && (col.venue || col.durStr) && (
              <div className="text-[10px] text-slate-400 mt-0.5 pl-4">{[col.venue, col.durStr].filter(Boolean).join(' · ')}</div>
            )}
            {/* Enrollment editor (group courses only) */}
            {col.type === 'group' && col.enrollment && (
              <div className="flex items-center gap-1.5 mt-1.5 pl-1 flex-wrap">
                <select value={col.enrollment.role || 'follower'}
                  onChange={e => handleEnrollmentUpdate(col.id, { role: e.target.value })}
                  className="h-5 px-1.5 rounded-full border border-blue-200 bg-blue-50 text-[9px] font-bold text-blue-600 focus:outline-none cursor-pointer"
                  data-testid={`enroll-role-${col.id}`}>
                  <option value="leader">Leader</option>
                  <option value="follower">Follower</option>
                </select>
                {(pricingMatrix?.length ?? 0) > 0 && (
                  <select value={col.enrollment.billing_status || 'Standard'}
                    onChange={e => handleEnrollmentUpdate(col.id, { billing_status: e.target.value })}
                    className={cn('h-5 px-1.5 rounded-full border text-[9px] font-bold focus:outline-none cursor-pointer',
                      col.enrollment.billing_status && col.enrollment.billing_status !== 'Standard'
                        ? 'border-orange-200 bg-orange-50 text-orange-600'
                        : 'border-slate-200 bg-slate-50 text-slate-500')}
                    data-testid={`enroll-billing-${col.id}`}>
                    {pricingMatrix.map(row => (
                      <option key={row.id || row.status_name} value={row.status_name}>{row.status_name}</option>
                    ))}
                  </select>
                )}
                <select
                  value={col.isEnrollActive ? 'active' : 'inactive'}
                  onChange={e => handleEnrollmentUpdate(col.id, { status: e.target.value })}
                  className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full border focus:outline-none cursor-pointer transition-colors',
                    col.isEnrollActive ? 'text-slate-400 border-slate-200 bg-white' : 'text-amber-600 border-amber-200 bg-amber-50')}
                  data-testid={`enroll-status-${col.id}`}>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            )}
            {col.type === 'private' && col.totalPastMinutes > 0 && (
              <div className="text-[10px] text-[#26A69A]/80 font-semibold mt-0.5 pl-4">
                {`${Math.floor(col.totalPastMinutes / 60)}h${col.totalPastMinutes % 60 || ''} effectuées`}
              </div>
            )}
          </div>
          {/* Passés */}
          {col.past.length > 0 && (
            <div className="px-3 pt-2 pb-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Passés · {col.past.length}</p>
              <div className="space-y-1.5">
                {col.past.map(cls => col.type === 'group'
                  ? <SessionRow key={cls.id} cls={cls} />
                  : <PrivRow key={cls.id} cls={cls} />
                )}
              </div>
            </div>
          )}
          {/* À venir */}
          {col.future.length > 0 && (
            <div className="px-3 pt-2 pb-3 border-t border-dashed border-slate-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#0288D1] mb-1.5">À venir · {col.future.length}</p>
              <div className="space-y-1.5">
                {col.future.map(cls => col.type === 'group'
                  ? <SessionRow key={cls.id} cls={cls} />
                  : <PrivRow key={cls.id} cls={cls} />
                )}
              </div>
            </div>
          )}
          {col.sessions.length === 0 && (
            <div className="px-3 py-4 text-[10px] text-slate-400 text-center italic">Aucune séance enregistrée</div>
          )}
        </div>
      ))}
    </div>
  );

  // Desktop: aligned multi-column grid
  const DesktopView = () => (
    <div style={{ display: 'grid', gridTemplateColumns: colWidths, columnGap: '12px' }}>

      {/* ROW 1: Column headers */}
      {displayCols.map((col, i) => (
        <div key={`hdr-${col.id}`}
          style={{ gridColumn: i + 1, gridRow: 1, borderBottomColor: col.color }}
          className={cn('pb-2 border-b-2', col.isEnrollActive === false && 'opacity-70')}>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
            <span className="text-xs font-bold truncate" style={{ color: col.color }}>{col.name}</span>
            {col.isEnrollActive === false && <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1 rounded-full">inactif</span>}
            <span className="text-[10px] text-slate-400 ml-auto shrink-0">{col.sessions.length}</span>
          </div>
          {col.type === 'group' && (col.venue || col.durStr) && (
            <div className="text-[10px] text-slate-400 mt-0.5 pl-4">
              {[col.venue, col.durStr].filter(Boolean).join(' · ')}
            </div>
          )}
          {/* Enrollment editor */}
          {col.type === 'group' && col.enrollment && (
            <div className="flex items-center gap-1 mt-1.5 pl-1 flex-wrap">
              <select value={col.enrollment.role || 'follower'}
                onChange={e => handleEnrollmentUpdate(col.id, { role: e.target.value })}
                className="h-5 px-1.5 rounded-full border border-blue-200 bg-blue-50 text-[9px] font-bold text-blue-600 focus:outline-none cursor-pointer"
                data-testid={`enroll-role-${col.id}`}>
                <option value="leader">Leader</option>
                <option value="follower">Follower</option>
              </select>
              {(pricingMatrix?.length ?? 0) > 0 && (
                <select value={col.enrollment.billing_status || 'Standard'}
                  onChange={e => handleEnrollmentUpdate(col.id, { billing_status: e.target.value })}
                  className={cn('h-5 px-1.5 rounded-full border text-[9px] font-bold focus:outline-none cursor-pointer',
                    col.enrollment.billing_status && col.enrollment.billing_status !== 'Standard'
                      ? 'border-orange-200 bg-orange-50 text-orange-600'
                      : 'border-slate-200 bg-slate-50 text-slate-500')}
                  data-testid={`enroll-billing-${col.id}`}>
                  {pricingMatrix.map(row => (
                    <option key={row.id || row.status_name} value={row.status_name}>{row.status_name}</option>
                  ))}
                </select>
              )}
              <select
                value={col.isEnrollActive ? 'active' : 'inactive'}
                onChange={e => handleEnrollmentUpdate(col.id, { status: e.target.value })}
                className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full border focus:outline-none cursor-pointer transition-colors',
                  col.isEnrollActive ? 'text-slate-400 border-slate-200 bg-white' : 'text-amber-600 border-amber-200 bg-amber-50')}
                data-testid={`enroll-status-${col.id}`}>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          )}
          {col.type === 'private' && col.totalPastMinutes > 0 && (
            <div className="text-[10px] text-[#26A69A]/80 font-semibold mt-0.5 pl-4">
              {`${Math.floor(col.totalPastMinutes / 60)}h${col.totalPastMinutes % 60 || ''} effectuées`}
            </div>
          )}
        </div>
      ))}

      {/* ROW 2: "Passés" labels */}
      {displayCols.map((col, i) => (
        <div key={`past-lbl-${col.id}`}
          style={{ gridColumn: i + 1, gridRow: 2 }}
          className="flex items-center gap-2 px-1 pt-2 pb-1 min-h-[28px]">
          {col.past.length > 0 && (
            <>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Passés</span>
              <span className="text-[10px] text-slate-300">{col.past.length}</span>
            </>
          )}
        </div>
      ))}

      {/* ROW 3: Past content */}
      {displayCols.map((col, i) => (
        <div key={`past-${col.id}`}
          style={{ gridColumn: i + 1, gridRow: 3 }}
          className="space-y-1">
          {col.past.map(cls =>
            col.type === 'group'
              ? <SessionRow key={cls.id} cls={cls} />
              : <PrivRow key={cls.id} cls={cls} />
          )}
        </div>
      ))}

      {/* ROW 4: "À venir" labels */}
      {displayCols.map((col, i) => (
        <div key={`future-lbl-${col.id}`}
          style={{ gridColumn: i + 1, gridRow: 4 }}
          className="flex items-center gap-2 px-1 pt-3 pb-1 min-h-[28px]">
          {col.future.length > 0 && (
            <>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#0288D1]">À venir</span>
              <span className="text-[10px] text-slate-300">{col.future.length}</span>
            </>
          )}
        </div>
      ))}

      {/* ROW 5: Future content */}
      {displayCols.map((col, i) => (
        <div key={`future-${col.id}`}
          style={{ gridColumn: i + 1, gridRow: 5, maxHeight: col.future.length > 0 ? '200px' : undefined, overflowY: col.future.length > 0 ? 'auto' : undefined }}
          className="space-y-1">
          {col.future.map(cls =>
            col.type === 'group'
              ? <SessionRow key={cls.id} cls={cls} />
              : <PrivRow key={cls.id} cls={cls} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Toggle inactifs */}
      {inactiveCols.length > 0 && (
        <div className="flex justify-end mb-3">
          <button
            onClick={() => setShowInactiveEnrollments(v => {
              const next = !v;
              localStorage.setItem('courses_tab_show_inactive', JSON.stringify(next));
              return next;
            })}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors',
              showInactiveEnrollments
                ? 'bg-slate-100 border-slate-300 text-slate-600'
                : 'bg-white border-dashed border-slate-300 text-slate-400 hover:border-slate-400 hover:text-slate-500')}
            data-testid="toggle-inactive-enrollments">
            {showInactiveEnrollments ? `↑ Masquer ${inactiveCols.length} inactif${inactiveCols.length > 1 ? 's' : ''}` : `· ${inactiveCols.length} cours inactif${inactiveCols.length > 1 ? 's' : ''} ▼`}
          </button>
        </div>
      )}
      <div className="sm:hidden"><MobileView /></div>
      <div className="hidden sm:block"><DesktopView /></div>
    </>
  );
}

function PackageCard({ pkg, pkgPayments = [], financeData, siblingGroupPkgs = [], studentId, allAttendances = [], onRefresh, locale }) {
  const { t } = useLang();
  const [editing, setEditing] = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);
  const [showArchiveForm, setShowArchiveForm] = useState(false);
  const [archiveDate, setArchiveDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Private sessions
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [allPrivateSessions, setAllPrivateSessions] = useState(null);
  const [pendingIds, setPendingIds] = useState(null);
  const [saving, setSaving] = useState(false);
  // Group sessions
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [allGroupSessions, setAllGroupSessions] = useState(null);
  const [pendingGroupIds, setPendingGroupIds] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);

  const isArchived = !!pkg.archived_at;
  const isPrivateType = pkg.type === 'private' || pkg.type === 'mixed';
  const isGroupType = pkg.type === 'group' || pkg.type === 'mixed';
  const progress = pkg.total_price > 0 ? Math.round((pkg.paid_amount / pkg.total_price) * 100) : 100;
  const remaining = parseFloat(pkg.total_price) - parseFloat(pkg.paid_amount);

  // Eagerly load private sessions for accurate per-package progress bar
  useEffect(() => {
    if (isPrivateType && !isArchived) {
      api.get(`/private-sessions/student/${studentId}`)
        .then(data => setAllPrivateSessions(data))
        .catch(() => {});
    }
  }, [pkg.id, studentId, isPrivateType, isArchived]);

  // Eagerly load group sessions for accurate per-package progress bar
  useEffect(() => {
    if (isGroupType && !isArchived) {
      api.get(`/group-sessions/student/${studentId}`)
        .then(data => setAllGroupSessions(data))
        .catch(() => {});
    }
  }, [pkg.id, studentId, isGroupType, isArchived]);

  // Load private sessions and open picker
  const openSessionPicker = async () => {
    if (!allPrivateSessions) {
      const data = await api.get(`/private-sessions/student/${studentId}`);
      setAllPrivateSessions(data);
      // Compute initial pending selection
      initPending(data);
    } else {
      initPending(allPrivateSessions);
    }
    setShowSessionPicker(true);
  };

  const initPending = (sessions) => {
    // Already assigned to this package
    const already = sessions.filter(s => s.package_id === pkg.id).map(s => s.id);
    // Unassigned sessions (not in any package or only in this one)
    const unassigned = sessions.filter(s => !s.package_id || s.package_id === pkg.id)
      .sort((a, b) => b.datetime_start.localeCompare(a.datetime_start));
    // Propose most recent to fill remaining hours
    let proposed = [...already];
    let hoursInPkg = sessions.filter(s => s.package_id === pkg.id)
      .reduce((sum, s) => sum + (s.duration_minutes || 60) / 60, 0);
    const remaining_h = (pkg.total_hours || 0) - hoursInPkg;
    if (remaining_h > 0) {
      let filled = 0;
      for (const s of unassigned) {
        if (proposed.includes(s.id)) continue;
        const dur = (s.duration_minutes || 60) / 60;
        if (filled + dur <= remaining_h + 0.01) {
          proposed.push(s.id);
          filled += dur;
        }
      }
    }
    setPendingIds(proposed);
  };

  const toggleSession = (sessionId) => {
    setPendingIds(prev =>
      prev.includes(sessionId) ? prev.filter(id => id !== sessionId) : [...prev, sessionId]
    );
  };

  const saveSessionAssignment = async () => {
    if (!allPrivateSessions) return;
    setSaving(true);
    const wasAssigned = allPrivateSessions.filter(s => s.package_id === pkg.id).map(s => s.id);
    const toAssign = pendingIds.filter(id => !wasAssigned.includes(id));
    const toDeassign = wasAssigned.filter(id => !pendingIds.includes(id));
    await api.put(`/packages/${pkg.id}/sessions`, {
      student_id: studentId,
      class_slot_ids: toAssign,
      deassign_ids: toDeassign
    });
    const fresh = await api.get(`/private-sessions/student/${studentId}`);
    setAllPrivateSessions(fresh);
    setSaving(false);
    setShowSessionPicker(false);
    toast.success('Séances privées assignées');
    onRefresh();
  };

  // Group session picker logic
  const initPendingGroup = (sessions) => {
    const already = sessions.filter(s => s.package_id === pkg.id).map(s => s.id);
    const available = sessions.filter(s => !s.package_id || s.package_id === pkg.id)
      .sort((a, b) => a.datetime_start.localeCompare(b.datetime_start));
    let proposed = [...already];
    const alreadyCount = already.length;
    const remaining_c = effectiveTotal - alreadyCount;
    if (remaining_c > 0) {
      let filled = 0;
      for (const s of available) {
        if (proposed.includes(s.id)) continue;
        if (filled < remaining_c) { proposed.push(s.id); filled++; }
      }
    }
    setPendingGroupIds(proposed);
  };

  const openGroupPicker = async () => {
    let sessions = allGroupSessions;
    if (!sessions) {
      sessions = await api.get(`/group-sessions/student/${studentId}`);
      setAllGroupSessions(sessions);
    }
    initPendingGroup(sessions);
    setShowGroupPicker(true);
  };

  const toggleGroupSession = (sessionId) => {
    setPendingGroupIds(prev =>
      prev.includes(sessionId) ? prev.filter(id => id !== sessionId) : [...prev, sessionId]
    );
  };

  const saveGroupAssignment = async () => {
    if (!allGroupSessions) return;
    setSavingGroup(true);
    const wasAssigned = allGroupSessions.filter(s => s.package_id === pkg.id).map(s => s.id);
    const toAssign = pendingGroupIds.filter(id => !wasAssigned.includes(id));
    const toDeassign = wasAssigned.filter(id => !pendingGroupIds.includes(id));
    await api.put(`/packages/${pkg.id}/sessions`, {
      student_id: studentId,
      class_slot_ids: toAssign,
      deassign_ids: toDeassign
    });
    const fresh = await api.get(`/group-sessions/student/${studentId}`);
    setAllGroupSessions(fresh);
    setSavingGroup(false);
    setShowGroupPicker(false);
    toast.success('Séances collectifs assignées');
    onRefresh();
  };

  const assignedSessions = allPrivateSessions?.filter(s => s.package_id === pkg.id) || [];
  const assignedHours = assignedSessions.reduce((sum, s) => sum + (s.duration_minutes || 60) / 60, 0);
  const assignedGroupSessions = allGroupSessions?.filter(s => s.package_id === pkg.id) || [];
  const assignedGroupCount = assignedGroupSessions.length;
  const fmtHours = (h) => {
    const total = Math.round(h * 60);
    const hrs = Math.floor(total / 60);
    const mins = total % 60;
    return mins ? `${hrs}h${String(mins).padStart(2, '0')}` : `${hrs}h`;
  };

  // Sessions progress — per-package using explicit assignments
  const isPrivType = pkg.type === 'private' || pkg.type === 'mixed';
  // Per-package hours: use assignedHours when sessions are loaded (accurate), else null
  const pkgHoursRemaining = (!isArchived && pkg.is_active && isPrivType && allPrivateSessions !== null)
    ? Math.max(0, (pkg.total_hours || 0) - assignedHours)
    : null;
  const effectiveTotal = isGroupType ? Math.max(0, (pkg.total_classes || 0) - (pkg.classes_done_at_creation || 0)) : 0;

  // Per-package group classes: use explicit assignments when loaded, else distribution fallback
  const classesDoneForPkg = isGroupType
    ? (allGroupSessions !== null
        ? assignedGroupCount
        : (() => {
            // Fallback distribution while loading
            const totalGroupDone = financeData?.group_sessions_done ?? 0;
            const priorGroupEffective = siblingGroupPkgs
              .filter(p => (p.created_at || '') < (pkg.created_at || '') || (p.created_at === pkg.created_at && p.id < pkg.id))
              .reduce((sum, p) => sum + Math.max(0, (p.total_classes || 0) - (p.classes_done_at_creation || 0)), 0);
            return Math.max(0, Math.min(effectiveTotal, totalGroupDone - priorGroupEffective));
          })())
    : null;
  const classesRemainingForPkg = (classesDoneForPkg !== null && effectiveTotal > 0)
    ? Math.max(0, effectiveTotal - classesDoneForPkg)
    : null;
  const sessionPct = (effectiveTotal > 0 && classesDoneForPkg !== null)
    ? Math.round((classesDoneForPkg / effectiveTotal) * 100) : null;

  // For archived packages: compute sessions consumed within the package period
  const archivedStats = isArchived ? (() => {
    const start = pkg.created_at;
    const end = pkg.archived_at;
    const inPeriod = allAttendances.filter(c => {
      const d = c.datetime_start;
      const att = c.attendances?.find(a => a.student_id === studentId);
      return att?.present && d >= start && d <= end;
    });
    const groupCount = inPeriod.filter(c => c.type === 'group').length;
    const privHours = inPeriod.filter(c => c.type === 'private')
      .reduce((sum, c) => sum + (c.duration_minutes || 0) / 60, 0);
    return { groupCount, privHours: Math.round(privHours * 10) / 10 };
  })() : null;

  const fmtD = (iso) => { try { return format(parseISO(iso), 'dd/MM/yy'); } catch { return ''; } };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce forfait ? Il va dans la Corbeille (restaurable).')) return;
    await api.delete(`/packages/${pkg.id}`);
    toast.success('Forfait supprimé');
    onRefresh();
  };

  const handleDeletePayment = async (pid) => {
    await api.delete(`/payments/${pid}`);
    toast.success('Paiement supprimé');
    onRefresh();
  };

  const handleToggleActive = async () => {
    await api.put(`/packages/${pkg.id}`, { is_active: !pkg.is_active });
    onRefresh();
  };

  const handleArchive = async () => {
    await api.put(`/packages/${pkg.id}`, { is_active: false, archived_at: archiveDate });
    toast.success('Forfait archivé');
    setShowArchiveForm(false);
    onRefresh();
  };

  if (editing) {
    return <PackageForm studentId={pkg.student_id} existingPkg={pkg} onSaved={() => { setEditing(false); onRefresh(); }} onCancel={() => setEditing(false)} />;
  }

  // ── Archived (read-only) view ──
  if (isArchived) {
    return (
      <div className="bg-slate-50 rounded-2xl border border-slate-200 opacity-75 transition-opacity hover:opacity-90" data-testid="package-card-archived">
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full',
                pkg.type === 'group' ? 'bg-[#0288D1]/10 text-[#0288D1]' :
                pkg.type === 'mixed' ? 'bg-violet-100 text-violet-600' :
                'bg-[#26A69A]/10 text-[#26A69A]')}>
                {pkg.type === 'group' ? 'Collectif' : pkg.type === 'mixed' ? 'Mixte' : 'Privé'}
              </span>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500 flex items-center gap-1">
                <Archive size={9} /> Archivé
              </span>
            </div>
            <button onClick={handleDelete} title="Supprimer (va dans la Corbeille)"
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors"
              data-testid="delete-package-btn">
              <Trash2 size={13} className="text-red-300" />
            </button>
          </div>

          {/* Date range */}
          <div className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
            <CalendarDays size={11} className="text-slate-400" />
            <span className="font-semibold">du {fmtD(pkg.created_at)} au {fmtD(pkg.archived_at)}</span>
          </div>

          {/* Sessions consumed in period */}
          {archivedStats && (archivedStats.groupCount > 0 || archivedStats.privHours > 0) && (
            <div className="flex items-center gap-2 mb-2">
              {archivedStats.groupCount > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#0288D1]/10 text-[#0288D1]">
                  {archivedStats.groupCount} CC effectués
                </span>
              )}
              {archivedStats.privHours > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#26A69A]/10 text-[#26A69A]">
                  {archivedStats.privHours}h CP effectuées
                </span>
              )}
            </div>
          )}

          {/* Package details summary */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{pkg.total_classes ? `${pkg.total_classes} cours` : ''}{pkg.total_hours ? `${pkg.total_hours}h` : ''}</span>
            <span className={cn('font-bold', remaining <= 0 ? 'text-[#26A69A]' : 'text-amber-500')}>
              {remaining <= 0 ? '✓ Soldé' : `${remaining} CHF restants`}
            </span>
          </div>
        </div>

        {/* Payments (collapsed, show only count) */}
        {pkgPayments.length > 0 && (
          <div className="border-t border-slate-200 px-4 py-2 text-xs text-slate-400">
            {pkgPayments.length} paiement{pkgPayments.length > 1 ? 's' : ''} · {pkg.paid_amount} CHF payé{pkg.paid_amount !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('bg-white rounded-2xl border shadow-sm transition-opacity', pkg.is_active ? 'border-slate-100' : 'border-slate-100 opacity-50')} data-testid="package-card">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full',
              pkg.type === 'group' ? 'bg-[#0288D1]/10 text-[#0288D1]' :
              pkg.type === 'mixed' ? 'bg-violet-100 text-violet-600' :
              'bg-[#26A69A]/10 text-[#26A69A]')}>
              {pkg.type === 'group' ? 'Collectif' : pkg.type === 'mixed' ? 'Mixte' : 'Privé'}
            </span>
            {!pkg.is_active && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400">Inactif</span>}
            {(pkg.type === 'group' || pkg.type === 'mixed') && pkg.classes_done_at_creation > 0 && (
              <span className="text-[10px] text-amber-500 font-semibold bg-amber-50 px-1.5 py-0.5 rounded-full">
                {pkg.classes_done_at_creation} déjà effectués
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {pkg.is_active && (
              <button onClick={() => setShowArchiveForm(v => !v)} title="Archiver ce forfait"
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="archive-package-btn">
                <Archive size={13} className="text-slate-400" />
              </button>
            )}
            <button onClick={handleToggleActive} title={pkg.is_active ? 'Désactiver' : 'Activer'}
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="toggle-active-btn">
              <CheckCircle size={14} className={pkg.is_active ? 'text-[#26A69A]' : 'text-slate-300'} />
            </button>
            <button onClick={() => setEditing(true)} title="Modifier"
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#0288D1]/10 transition-colors"
              data-testid="edit-package-btn">
              <Edit2 size={13} className="text-[#0288D1]" />
            </button>
            <button onClick={handleDelete} title="Supprimer"
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors"
              data-testid="delete-package-btn">
              <Trash2 size={13} className="text-red-400" />
            </button>
          </div>
        </div>

        {/* Archive form */}
        {showArchiveForm && (
          <div className="mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200" data-testid="archive-form">
            <div className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1.5">
              <Archive size={11} /> Date de mise hors service
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={archiveDate}
                onChange={e => setArchiveDate(e.target.value)}
                className="flex-1 h-9 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300"
                data-testid="archive-date-input"
              />
              <button onClick={handleArchive}
                className="h-9 px-3 text-xs font-bold bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="confirm-archive-btn">
                Archiver
              </button>
              <button onClick={() => setShowArchiveForm(false)}
                className="h-9 px-3 text-xs font-semibold text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                Annuler
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">
              Les cours effectués avant cette date seront rattachés à ce forfait.
            </p>
          </div>
        )}
        {/* Sessions progress */}
        {isGroupType && pkg.total_classes > 0 && (
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-semibold text-slate-600">
                {classesDoneForPkg !== null ? `${classesDoneForPkg}/${effectiveTotal} cours effectués` : `${pkg.total_classes} cours collectifs`}
              </span>
              {classesRemainingForPkg !== null && (
                <span className={cn('text-xs font-bold', classesRemainingForPkg === 0 ? 'text-slate-400' : 'text-[#0288D1]')}>
                  {classesRemainingForPkg} restant{classesRemainingForPkg !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#0288D1] rounded-full transition-all"
                style={{ width: `${Math.min(100, sessionPct ?? 0)}%` }} />
            </div>
          </div>
        )}
        {isPrivType && pkg.total_hours > 0 && (
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-semibold text-slate-600">{pkg.total_hours}h privées total</span>
              {pkgHoursRemaining !== null && (
                <span className={cn('text-xs font-bold', pkgHoursRemaining === 0 ? 'text-slate-400' : 'text-[#26A69A]')}>
                  {pkgHoursRemaining}h {pkgHoursRemaining <= 1 ? 'restante' : 'restantes'}
                </span>
              )}
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#26A69A] rounded-full transition-all"
                style={{ width: `${Math.min(100, pkg.total_hours > 0 ? (assignedHours / pkg.total_hours) * 100 : 0)}%` }} />
            </div>
          </div>
        )}

        {/* Payment progress */}
        <div className="mb-1">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-semibold text-slate-600">{pkg.total_price} CHF</span>
            <span className={cn('text-xs font-bold', remaining > 0 ? 'text-red-500' : 'text-[#26A69A]')}>
              {remaining > 0 ? `-${remaining.toFixed(0)} CHF dû` : 'Soldé ✓'}
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2 mb-1">
            <div className={cn('h-2 rounded-full transition-all', progress === 100 ? 'bg-[#26A69A]' : progress > 50 ? 'bg-amber-400' : 'bg-red-400')}
              style={{ width: `${Math.min(100, progress)}%` }} />
          </div>
          <div className="text-xs text-slate-400">{pkg.paid_amount} CHF payés · {progress}%</div>
        </div>
        {pkg.notes && <div className="text-xs text-slate-400 italic mt-1">{pkg.notes}</div>}
      </div>

      {/* ── Private sessions assignment (for private/mixed packages) ── */}
      {isPrivateType && !isArchived && (
        <div className="border-t border-slate-50 px-4 py-3" data-testid="sessions-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-600">Séances privées</span>
              {allPrivateSessions && (
                <span className="text-[10px] font-bold text-slate-400">
                  {fmtHours(assignedHours)} / {pkg.total_hours}h assignées
                </span>
              )}
            </div>
            <button
              onClick={showSessionPicker ? () => setShowSessionPicker(false) : openSessionPicker}
              className="text-xs font-semibold text-[#0288D1] hover:bg-[#0288D1]/5 px-2 py-1 rounded-lg transition-colors"
              data-testid="manage-sessions-btn"
            >
              {showSessionPicker ? 'Fermer' : allPrivateSessions ? 'Modifier' : 'Assigner des séances'}
            </button>
          </div>

          {/* Assigned sessions summary (when picker is closed) */}
          {!showSessionPicker && allPrivateSessions && assignedSessions.length > 0 && (
            <div className="space-y-0.5">
              {assignedSessions.map(s => (
                <div key={s.id} className="flex items-center justify-between text-[10px] text-slate-500 bg-[#26A69A]/5 rounded-lg px-2 py-1">
                  <span className="font-semibold">
                    {(() => { try { return format(parseISO(s.datetime_start), 'dd/MM/yy · HH:mm'); } catch { return s.datetime_start?.slice(0, 10); } })()}
                  </span>
                  <span className="text-[#26A69A] font-bold">
                    {s.duration_minutes ? `${Math.floor(s.duration_minutes/60)}h${s.duration_minutes%60 || ''}` : '1h'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Session picker */}
          {showSessionPicker && allPrivateSessions && (
            <div data-testid="session-picker">
              {allPrivateSessions.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-3">Aucune séance privée trouvée</div>
              ) : (
                <>
                  <div className="text-[10px] text-slate-400 mb-1.5">
                    Séances proposées pré-cochées · modifiez si besoin
                  </div>
                  <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                    {allPrivateSessions.map(s => {
                      const isSelected = pendingIds?.includes(s.id);
                      const isOtherPkg = s.package_id && s.package_id !== pkg.id;
                      const dur = s.duration_minutes
                        ? `${Math.floor(s.duration_minutes/60)}h${s.duration_minutes%60 || ''}`
                        : '1h';
                      const dateStr = (() => { try { return format(parseISO(s.datetime_start), 'dd/MM/yy · HH:mm'); } catch { return s.datetime_start?.slice(0, 10); } })();
                      return (
                        <label key={s.id}
                          className={cn(
                            'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors text-[10px]',
                            isSelected ? 'bg-[#26A69A]/10 border border-[#26A69A]/30' : 'bg-white border border-slate-100 hover:bg-slate-50',
                            isOtherPkg && 'opacity-50'
                          )}>
                          <input type="checkbox" checked={!!isSelected} disabled={isOtherPkg}
                            onChange={() => !isOtherPkg && toggleSession(s.id)}
                            className="accent-[#26A69A] w-3 h-3 shrink-0" />
                          <span className="font-semibold text-slate-700 flex-1">{dateStr}</span>
                          <span className={cn('font-bold', isSelected ? 'text-[#26A69A]' : 'text-slate-400')}>{dur}</span>
                          {isOtherPkg && <span className="text-[9px] text-amber-400 font-bold">autre forfait</span>}
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400">
                      {pendingIds ? fmtHours(
                        allPrivateSessions.filter(s => pendingIds.includes(s.id))
                          .reduce((sum, s) => sum + (s.duration_minutes || 60) / 60, 0)
                      ) : '0h'} sélectionnées
                    </span>
                    <button onClick={saveSessionAssignment} disabled={saving}
                      className="h-8 px-3 text-xs font-bold bg-[#26A69A] text-white rounded-lg hover:bg-[#26A69A]/90 transition-colors disabled:opacity-50"
                      data-testid="save-sessions-btn">
                      {saving ? '...' : 'Valider'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Group sessions assignment (for group/mixed packages) ── */}
      {isGroupType && !isArchived && (
        <div className="border-t border-slate-50 px-4 py-3" data-testid="group-sessions-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-600">Séances collectifs</span>
              {allGroupSessions !== null && (
                <span className="text-[10px] font-bold text-slate-400">
                  {assignedGroupCount} / {effectiveTotal} assignées
                </span>
              )}
            </div>
            <button
              onClick={showGroupPicker ? () => setShowGroupPicker(false) : openGroupPicker}
              className="text-xs font-semibold text-[#0288D1] hover:bg-[#0288D1]/5 px-2 py-1 rounded-lg transition-colors"
              data-testid="manage-group-sessions-btn"
            >
              {showGroupPicker ? 'Fermer' : allGroupSessions ? 'Modifier' : 'Assigner des séances'}
            </button>
          </div>

          {/* Assigned sessions summary */}
          {!showGroupPicker && allGroupSessions && assignedGroupSessions.length > 0 && (
            <div className="space-y-0.5">
              {assignedGroupSessions.map(s => {
                const dateStr = (() => { try { return format(parseISO(s.datetime_start), 'dd/MM/yy'); } catch { return s.datetime_start?.slice(0, 10); } })();
                const courseName = s.name || s.group_course_name || '';
                return (
                  <div key={s.id} className="flex items-center justify-between text-[10px] text-slate-500 bg-[#0288D1]/5 rounded-lg px-2 py-1">
                    <span className="font-semibold">{dateStr}</span>
                    {courseName && <span className="text-slate-400 truncate mx-2 flex-1">{courseName}</span>}
                    <span className="text-[#0288D1] font-bold shrink-0">
                      {s.duration_minutes ? `${Math.floor(s.duration_minutes/60)}h${s.duration_minutes%60 || ''}` : '1h30'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Group session picker */}
          {showGroupPicker && allGroupSessions && (
            <div data-testid="group-session-picker">
              {allGroupSessions.length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-3">Aucune séance collective trouvée</div>
              ) : (
                <>
                  <div className="text-[10px] text-slate-400 mb-1.5">
                    Séances auto-assignées · modifiez si besoin
                  </div>
                  <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
                    {allGroupSessions.map(s => {
                      const isSelected = pendingGroupIds?.includes(s.id);
                      const isOtherPkg = s.package_id && s.package_id !== pkg.id;
                      const dateStr = (() => { try { return format(parseISO(s.datetime_start), 'dd/MM/yy'); } catch { return s.datetime_start?.slice(0, 10); } })();
                      const courseName = s.name || s.group_course_name || '';
                      return (
                        <label key={s.id}
                          className={cn(
                            'flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors text-[10px]',
                            isSelected ? 'bg-[#0288D1]/10 border border-[#0288D1]/30' : 'bg-white border border-slate-100 hover:bg-slate-50',
                            isOtherPkg && 'opacity-50'
                          )}>
                          <input type="checkbox" checked={!!isSelected} disabled={isOtherPkg}
                            onChange={() => !isOtherPkg && toggleGroupSession(s.id)}
                            className="accent-[#0288D1] w-3 h-3 shrink-0" />
                          <span className="font-semibold text-slate-700">{dateStr}</span>
                          {courseName && <span className="text-slate-400 truncate flex-1">{courseName}</span>}
                          {isOtherPkg && <span className="text-[9px] text-amber-400 font-bold ml-auto">autre forfait</span>}
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-400">
                      {pendingGroupIds?.length ?? 0} sélectionnée{(pendingGroupIds?.length ?? 0) !== 1 ? 's' : ''}
                    </span>
                    <button onClick={saveGroupAssignment} disabled={savingGroup}
                      className="h-8 px-3 text-xs font-bold bg-[#0288D1] text-white rounded-lg hover:bg-[#0288D1]/90 transition-colors disabled:opacity-50"
                      data-testid="save-group-sessions-btn">
                      {savingGroup ? '...' : 'Valider'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payment history */}
      {pkgPayments.length > 0 && (
        <div className="border-t border-slate-50 px-4 py-2 space-y-1">
          {pkgPayments.sort((a, b) => b.date.localeCompare(a.date)).map(p => (
            <div key={p.id} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-slate-600">
                <span className="font-bold text-[#26A69A]">+{p.amount} CHF</span>
                <span className="text-slate-400">
                  {(() => { try { return format(parseISO(p.date), 'd MMM yyyy', { locale }); } catch { return p.date; } })()}
                  {' · '}{p.method}
                </span>
              </div>
              <button onClick={() => handleDeletePayment(p.id)}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 transition-colors"
                data-testid="delete-payment-btn">
                <Trash2 size={11} className="text-red-300 hover:text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add payment inline */}
      <div className="border-t border-slate-50 px-4 py-2">
        {showPayForm ? (
          <QuickPayForm packageId={pkg.id} studentId={studentId}
            onSaved={() => { setShowPayForm(false); onRefresh(); }}
            onCancel={() => setShowPayForm(false)} />
        ) : (
          remaining > 0 ? (
            <button onClick={() => setShowPayForm(true)}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-bold text-[#0288D1] py-1.5 hover:bg-[#0288D1]/5 rounded-lg transition-colors"
              data-testid="add-payment-pkg-btn">
              <Plus size={13} /> Enregistrer un paiement
            </button>
          ) : (
            <div className="text-center text-xs text-slate-300 py-1">Soldé</div>
          )
        )}
      </div>
    </div>
  );
}

function QuickPayForm({ packageId, studentId, onSaved, onCancel }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setSaving(true);
    try {
      await api.post('/payments', {
        student_id: studentId,
        package_id: packageId,
        amount: parseFloat(amount),
        method,
        date: new Date().toISOString(),
        notes: '',
      });
      toast.success('Paiement enregistré');
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-2 py-1" data-testid="quick-pay-form">
      <input
        type="number" value={amount} onChange={e => setAmount(e.target.value)}
        placeholder="Montant CHF" autoFocus
        className="flex-1 h-8 px-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
        onKeyDown={e => e.key === 'Enter' && handleSave()}
      />
      <select value={method} onChange={e => setMethod(e.target.value)}
        className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
        <option value="cash">Cash</option>
        <option value="twint">Twint</option>
        <option value="virement">Virement</option>
        <option value="carte">Carte</option>
      </select>
      <button onClick={handleSave} disabled={saving}
        className="w-8 h-8 flex items-center justify-center bg-[#26A69A] text-white rounded-lg hover:bg-[#219e92] transition-colors text-xs font-bold"
        data-testid="confirm-payment-btn">
        {saving ? '…' : '✓'}
      </button>
      <button onClick={onCancel}
        className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors text-xs">
        ✗
      </button>
    </div>
  );
}

function PackageForm({ studentId, existingPkg, onSaved, onCancel }) {
  const { t } = useLang();
  const isEdit = !!existingPkg;
  const [form, setForm] = useState(isEdit ? {
    type: existingPkg.type,
    total_classes: existingPkg.total_classes || 10,
    total_hours: existingPkg.total_hours || '',
    classes_done_at_creation: existingPkg.classes_done_at_creation || 0,
    total_price: existingPkg.total_price || '',
    paid_amount: existingPkg.paid_amount || 0,
    notes: existingPkg.notes || '',
  } : { type: 'group', total_classes: 10, total_hours: '', classes_done_at_creation: 0, total_price: '', paid_amount: 0, notes: '' });
  const [saving, setSaving] = useState(false);

  const showClasses = form.type === 'group' || form.type === 'mixed';
  const showHours = form.type === 'private' || form.type === 'mixed';

  const handleSave = async () => {
    if (!form.total_price) return;
    setSaving(true);
    try {
      const data = {
        type: form.type,
        total_price: parseFloat(form.total_price),
        paid_amount: parseFloat(form.paid_amount) || 0,
        notes: form.notes,
        classes_done_at_creation: showClasses ? (parseInt(form.classes_done_at_creation) || 0) : 0,
      };
      if (showClasses) data.total_classes = parseInt(form.total_classes) || 10;
      if (showHours) data.total_hours = parseFloat(form.total_hours) || 1;
      if (isEdit) {
        await api.put(`/packages/${existingPkg.id}`, data);
        toast.success('Forfait modifié');
      } else {
        await api.post('/packages', { ...data, student_id: studentId });
        toast.success('Forfait ajouté');
      }
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 mb-3 space-y-3">
      {/* Type selector */}
      <div className="flex gap-2">
        {[['group', 'Collectif'], ['private', 'Privé'], ['mixed', 'Mixte']].map(([val, label]) => (
          <button key={val} onClick={() => setForm(f => ({ ...f, type: val }))}
            className={cn('flex-1 py-2 rounded-lg text-sm font-semibold transition-colors',
              form.type === val
                ? val === 'mixed' ? 'bg-violet-500 text-white' : 'bg-[#0288D1] text-white'
                : 'bg-white border border-slate-200 text-slate-600')}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {/* Group classes */}
        {showClasses && (
          <FormField label="Nb de cours collectifs" type="number" value={form.total_classes}
            onChange={v => setForm(f => ({ ...f, total_classes: v }))} placeholder="ex: 10" />
        )}
        {/* Private hours */}
        {showHours && (
          <FormField label="Nb d'heures privées" type="number" step="0.5" value={form.total_hours}
            onChange={v => setForm(f => ({ ...f, total_hours: v }))} placeholder="ex: 5" />
        )}
        {/* Price */}
        <FormField label={t('pkg.price')} type="number" value={form.total_price}
          onChange={v => setForm(f => ({ ...f, total_price: v }))} placeholder="ex: 500" />
        {/* Already paid */}
        <FormField label={t('pkg.paid')} type="number" value={form.paid_amount}
          onChange={v => setForm(f => ({ ...f, paid_amount: v }))} placeholder="0" />
        {/* Classes already done at creation */}
        {showClasses && (
          <div className="col-span-2">
            <FormField label="Cours collectifs déjà effectués à la création" type="number"
              value={form.classes_done_at_creation}
              onChange={v => setForm(f => ({ ...f, classes_done_at_creation: v }))} placeholder="0" />
            <p className="text-[10px] text-slate-400 mt-1">
              Indiquez combien de cours ont déjà été faits avant la saisie de ce forfait. Ils seront déduits du compteur.
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 h-10 rounded-xl bg-white border border-slate-200 text-slate-600 text-sm font-semibold">{t('common.cancel')}</button>
        <button onClick={handleSave} disabled={saving} className="flex-1 h-10 rounded-xl bg-[#0288D1] text-white text-sm font-bold">{saving ? '...' : t('common.save')}</button>
      </div>
    </div>
  );
}

function FormField({ label, type = 'text', value, onChange, placeholder, step }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input
        type={type}
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
      />
    </div>
  );
}

function AttendancePairProfile({ classId, studentId, att, onChanged, compact = false }) {
  const getState = (a) => {
    if (!a) return 'absent';
    if (a.is_not_enrolled) return 'not_enrolled';
    if (a.is_aide) return 'aide';
    if (a.is_trial) return 'trial';
    if (a.present) return 'present';
    return 'absent';
  };
  const [state, setState] = useState(() => getState(att));
  const [saving, setSaving] = useState(false);
  const CYCLE = { absent: 'present', present: 'trial', trial: 'aide', aide: 'not_enrolled', not_enrolled: 'absent' };
  const CFG = {
    absent:       { label: 'Absent',      cls: 'bg-red-50 text-red-400 border-red-100 hover:bg-red-100' },
    present:      { label: 'Présent',     cls: 'bg-[#26A69A]/10 text-[#26A69A] border-[#26A69A]/20 hover:bg-[#26A69A]/20' },
    trial:        { label: 'Essai',       cls: 'bg-orange-50 text-orange-500 border-orange-200 hover:bg-orange-100' },
    aide:         { label: 'Aide',        cls: 'bg-purple-50 text-purple-500 border-purple-200 hover:bg-purple-100' },
    not_enrolled: { label: 'Non inscrit', cls: 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200' },
  };
  const toAtt = (s) => ({
    present: s === 'present' || s === 'trial' || s === 'aide',
    is_trial: s === 'trial',
    is_aide: s === 'aide',
    is_not_enrolled: s === 'not_enrolled',
  });
  const cycle = async () => {
    const next = CYCLE[state];
    setState(next);
    setSaving(true);
    try {
      await api.put(`/classes/${classId}/attendance`, [{ student_id: studentId, ...toAtt(next) }]);
      onChanged?.();
    } catch { setState(getState(att)); }
    finally { setSaving(false); }
  };
  const cfg = CFG[state];
  return (
    <button onClick={cycle} disabled={saving}
      className={cn('px-2.5 py-1 rounded-lg text-xs font-bold border transition-all', cfg.cls, saving && 'opacity-50')}
      data-testid={`att-cycle-profile-${classId}`}>
      {saving ? '…' : cfg.label}
    </button>
  );
}
